import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import { useApi } from "../../hooks/useApi";
import { getSuperServer, retryFailedJobs, flushFailedJobs } from "../../api/superAdminService";
import { showApiError, showToast } from "../../utils/toast";

function formatBytes(b) {
  if (b == null || Number.isNaN(b)) return "—";
  if (b < 1024) return `${b} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function ago(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export default function ServerPage() {
  const server = useApi(() => getSuperServer(), []);
  const d = server.data?.data;
  const disk = d?.disk || {};
  const drive = d?.drive || {};
  const php = d?.php || {};
  const app = d?.app || {};
  const queue = d?.queue || {};
  const sys = d?.system || {};
  const mem = sys.memory;
  const swap = sys.swap;

  const usedPct = disk.total ? Math.min(100, (disk.used / disk.total) * 100) : null;
  const diskTone = usedPct == null ? "ok" : usedPct >= 90 ? "danger" : usedPct >= 75 ? "warning" : "ok";

  const memPct = mem?.total ? Math.min(100, (mem.used / mem.total) * 100) : null;
  const memTone = memPct == null ? "ok" : memPct >= 90 ? "danger" : memPct >= 75 ? "warning" : "ok";
  const swapPct = swap?.total ? Math.min(100, (swap.used / swap.total) * 100) : null;
  // Heavy swap use on a small VM = RAM pressure, even if RAM "available" looks ok.
  const swapTone = swapPct == null ? "ok" : swapPct >= 50 ? "danger" : swapPct >= 20 ? "warning" : "ok";
  // Load relative to cores: >1.0 per core means the CPU is saturated.
  const loadRatio = sys.load && sys.cpu_cores ? sys.load["1m"] / sys.cpu_cores : null;
  const loadTone = loadRatio == null ? "ok" : loadRatio >= 1 ? "danger" : loadRatio >= 0.7 ? "warning" : "ok";

  const retry = async () => {
    try { await retryFailedJobs(); showToast("success", "Failed jobs queued for retry"); server.reload(); }
    catch (err) { showApiError(err); }
  };
  const flush = async () => {
    if (!window.confirm("Permanently delete all failed job records? This cannot be undone.")) return;
    try { await flushFailedJobs(); showToast("success", "Failed jobs cleared"); server.reload(); }
    catch (err) { showApiError(err); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Server</h1>
          <div className="page-sub">Platform disk, runtime, and background queue</div>
        </div>
        <div className="row gap-sm">
          <button className="btn btn-secondary" onClick={() => server.reload()} disabled={server.loading}>
            <Icon name="refresh" size={13} /> {server.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {server.loading && !d ? (
        <LoadingBlock rows={6} height={80} />
      ) : (
        <div className="col gap-md">
          {/* CPU + memory */}
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">CPU &amp; memory</div>
                <div className="card-sub">{sys.cpu_cores ? `${sys.cpu_cores} vCPU` : "Live host load"}</div>
              </div>
            </div>
            <div className="card-body col gap-md">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                <Stat label="Load (1m)" value={sys.load ? sys.load["1m"] : "—"} tone={loadTone} />
                <Stat label="Load 5m / 15m" value={sys.load ? `${sys.load["5m"]} / ${sys.load["15m"]}` : "—"} />
                <Stat label="RAM used" value={mem ? `${formatBytes(mem.used)} / ${formatBytes(mem.total)}` : "—"} tone={memTone} />
                <Stat label="Swap used" value={swap ? `${formatBytes(swap.used)} / ${formatBytes(swap.total)}` : "—"} tone={swapTone} />
              </div>
              {memPct != null && (
                <div>
                  <div style={kpiLabel}>RAM ({memPct.toFixed(0)}%)</div>
                  <div style={{ height: 8, borderRadius: 6, background: "var(--surface-2)", overflow: "hidden", marginTop: 4 }}>
                    <div style={{ width: `${memPct}%`, height: "100%", background: memTone === "danger" ? "var(--danger)" : memTone === "warning" ? "var(--warning)" : "var(--success)" }} />
                  </div>
                </div>
              )}
              {/* Only warn once swap is genuinely high. A few hundred MB of
                  swap with RAM free is normal Linux behaviour (cold pages),
                  not pressure — so stay quiet below the warning threshold. */}
              {swapPct != null && swapPct >= 20 && (
                <div className="text-xs" style={{ color: swapTone === "danger" ? "var(--danger)" : "var(--warning)" }}>
                  Swap at {swapPct.toFixed(0)}% — the box is leaning on swap, a sign it's short on RAM. If this stays high, size up.
                </div>
              )}
              {loadTone === "danger" && (
                <div className="text-xs" style={{ color: "var(--danger)" }}>
                  Load exceeds CPU core count — the server is saturated. Sustained high load means it's time to size up.
                </div>
              )}
              {!sys.load && !mem && (
                <div className="text-xs t-muted">Host metrics unavailable (non-Linux host).</div>
              )}
            </div>
          </div>

          {/* Queue / worker health */}
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">Background queue</div>
                <div className="card-sub">Connection: {queue.connection || "—"}</div>
              </div>
              <div className="row gap-sm">
                <button className="btn btn-secondary btn-sm" onClick={retry} disabled={!queue.failed_total}>
                  <Icon name="refresh" size={13} /> Retry failed
                </button>
                <button className="btn btn-secondary btn-sm" onClick={flush} disabled={!queue.failed_total} style={{ color: "var(--danger)" }}>
                  <Icon name="trash" size={13} /> Clear failed
                </button>
              </div>
            </div>
            <div className="card-body col gap-md">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                <div>
                  <div style={kpiLabel}>Worker</div>
                  <div style={{ ...kpiValue, color: queue.worker_ok ? "var(--success)" : "var(--danger)" }}>
                    {queue.worker_ok ? "Healthy" : "Check"}
                  </div>
                </div>
                <Stat label="Pending jobs" value={(queue.pending ?? 0).toLocaleString()} />
                <Stat label="Oldest waiting" value={ago(queue.oldest_pending_seconds)} />
                <Stat label="Failed jobs" value={(queue.failed_total ?? 0).toLocaleString()} tone={queue.failed_total ? "danger" : "ok"} />
              </div>
              {!queue.worker_ok && (
                <div className="text-xs" style={{ color: "var(--danger)" }}>
                  Jobs are piling up and not draining — the queue worker may be down. Check the
                  <code> queue:work</code> process / its supervisor on the server.
                </div>
              )}

              <div>
                <div className="t-primary fw-600 text-sm" style={{ marginBottom: 6 }}>
                  Recent failures ({queue.failed_total ?? 0})
                </div>
                {!queue.recent_failed?.length ? (
                  <div className="t-muted text-xs">No failed jobs. 🎉</div>
                ) : (
                  <table className="table">
                    <thead>
                      <tr><th>Failed at</th><th>Queue</th><th>Error</th></tr>
                    </thead>
                    <tbody>
                      {queue.recent_failed.map((j) => (
                        <tr key={j.uuid}>
                          <td className="t-muted" style={{ whiteSpace: "nowrap" }}>{j.failed_at}</td>
                          <td className="t-mono text-xs">{j.queue}</td>
                          <td className="text-xs" style={{ fontFamily: "var(--font-mono)" }}>{j.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Disk */}
          <div className="card">
            <div className="card-head">
              <div>
                <div className="card-title">Disk usage</div>
                <div className="card-sub">Whole partition hosting app storage</div>
              </div>
            </div>
            <div className="card-body col gap-md">
              {usedPct != null && (
                <div style={{ height: 10, borderRadius: 6, background: "var(--surface-2)", overflow: "hidden" }}>
                  <div style={{ width: `${usedPct}%`, height: "100%", background: diskTone === "danger" ? "var(--danger)" : diskTone === "warning" ? "var(--warning)" : "var(--success)" }} />
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                <Stat label="Total" value={formatBytes(disk.total)} />
                <Stat label="Used" value={formatBytes(disk.used)} />
                <Stat label="Free" value={formatBytes(disk.free)} tone={diskTone} />
                <Stat label="% Used" value={usedPct == null ? "—" : `${usedPct.toFixed(1)}%`} tone={diskTone} />
              </div>
            </div>
          </div>

          {/* All-firms drive + runtime */}
          <div className="grid-2">
            <div className="card">
              <div className="card-head"><div className="card-title">Drive (all firms)</div></div>
              <div className="card-body">
                <KeyVal label="Files stored" value={(drive.file_count ?? 0).toLocaleString()} />
                <KeyVal label="Total size" value={formatBytes(drive.total_bytes)} />
                <KeyVal label="Share of disk" value={disk.total && drive.total_bytes != null ? `${((drive.total_bytes / disk.total) * 100).toFixed(2)}%` : "—"} />
              </div>
            </div>
            <div className="card">
              <div className="card-head"><div className="card-title">Runtime</div></div>
              <div className="card-body">
                <KeyVal label="PHP" value={php.version || "—"} />
                <KeyVal label="Laravel" value={app.laravel_version || "—"} />
                <KeyVal label="Environment" value={app.environment || "—"} />
                <KeyVal label="Timezone" value={app.timezone || "—"} />
                <KeyVal label="upload_max_filesize" value={php.upload_max_filesize || "—"} />
                <KeyVal label="post_max_size" value={php.post_max_size || "—"} />
                <KeyVal label="memory_limit" value={php.memory_limit || "—"} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const kpiLabel = { fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" };
const kpiValue = { fontSize: 22, fontWeight: 700, letterSpacing: -0.5, marginTop: 2 };

function Stat({ label, value, tone }) {
  const color = tone === "danger" ? "var(--danger)" : tone === "warning" ? "var(--warning)" : "var(--ink-900)";
  return (
    <div>
      <div style={kpiLabel}>{label}</div>
      <div style={{ ...kpiValue, color }}>{value ?? "—"}</div>
    </div>
  );
}

function KeyVal({ label, value }) {
  return (
    <div className="row" style={{ justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed var(--border)" }}>
      <span className="t-muted text-xs" style={{ fontFamily: "var(--font-mono)" }}>{label}</span>
      <span className="text-sm fw-600">{value}</span>
    </div>
  );
}
